import Phaser from 'phaser';
import { formatMoney, type SessionInitResponse, type HistoryRound } from '@gp/contracts';
import { str } from './opaque';
import { GameApi, GameApiError } from './api';
import { hasScene, isGeneric, loadGame } from './games';
import { mountGenericGame } from './generic/generic-game';
import { showLobby, hideLobby } from './lobby';
import type { SceneData } from './scene';

/**
 * Whatever is currently drawing the game.
 *
 * Some games have a Phaser scene and the rest are drawn from their schema as
 * plain DOM. The shell around them is the same either way, and the only
 * thing it asks of a screen is that it can be taken down again.
 */
interface Renderer {
  destroy(): void;
}

let activeRenderer: Renderer | null = null;
/** The Phaser instance, when the active renderer is one, for the mute button. */
let activeGame: Phaser.Game | null = null;
let activeApi: GameApi | null = null;
let activeSession: SessionInitResponse | null = null;

// ─── Notice overlay (kept from original) ────────────────────────────────────

interface NoticeAction {
  label: string;
  run: () => Promise<void>;
}

function showNotice(title: string, body: string, detail?: string, action?: NoticeAction): void {
  const notice = document.getElementById('notice');
  const card = document.getElementById('notice-card');
  if (!notice || !card) return;

  card.innerHTML = '';
  const heading = document.createElement('h1');
  heading.textContent = title;
  const paragraph = document.createElement('p');
  paragraph.textContent = body;
  card.append(heading, paragraph);

  if (action) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = action.label;
    const failure = document.createElement('p');

    button.addEventListener('click', () => {
      button.disabled = true;
      failure.textContent = '';
      void action.run().catch((error: unknown) => {
        button.disabled = false;
        failure.textContent =
          error instanceof GameApiError && error.code === 'GAME_DISABLED'
            ? 'Fun play is switched off on this deployment.'
            : error instanceof Error
              ? error.message
              : 'Could not start a fun session.';
      });
    });

    card.append(button, failure);
  }

  if (detail) {
    const code = document.createElement('code');
    code.textContent = detail;
    card.append(code);
  }

  notice.setAttribute('data-visible', 'true');
}

function hideNotice(): void {
  document.getElementById('notice')?.setAttribute('data-visible', 'false');
}

// ─── Game shell (DOM overlay around Phaser canvas) ───────────────────────────

function showShell(session: SessionInitResponse): void {
  const existing = document.getElementById('game-shell');
  if (existing) existing.remove();

  const shell = document.createElement('div');
  shell.id = 'game-shell';
  shell.innerHTML = `
    <div class="shell-top">
      <button class="shell-back" id="shell-back">← Lobby</button>
      <span class="shell-game-name">${session.game.name}</span>
      <span class="shell-balance" id="shell-balance">${formatMoney(session.balance, session.player.currency)}</span>
    </div>
    <div id="game"></div>
    <div class="shell-toolbar">
      <button class="shell-btn" id="btn-history">History</button>
      <button class="shell-btn" id="btn-paytable">Paytable</button>
      <button class="shell-btn" id="btn-sound">${getSoundMuted() ? '🔇' : '🔊'}</button>
    </div>
  `;
  document.body.appendChild(shell);

  document.getElementById('shell-back')!.addEventListener('click', returnToLobby);
  document.getElementById('btn-history')!.addEventListener('click', () => showHistory());
  document.getElementById('btn-paytable')!.addEventListener('click', () => showPaytable(session));
  document.getElementById('btn-sound')!.addEventListener('click', toggleSound);
}

function hideShell(): void {
  document.getElementById('game-shell')?.remove();
}

function updateShellBalance(balance: string, currency: string): void {
  const el = document.getElementById('shell-balance');
  if (el) el.textContent = formatMoney(balance, currency);
}

// ─── Sound toggle ────────────────────────────────────────────────────────────

function getSoundMuted(): boolean {
  return localStorage.getItem('gp-muted') === '1';
}

function toggleSound(): void {
  const muted = !getSoundMuted();
  localStorage.setItem('gp-muted', muted ? '1' : '0');
  if (activeGame) activeGame.sound.mute = muted;
  const btn = document.getElementById('btn-sound');
  if (btn) btn.textContent = muted ? '🔇' : '🔊';
}

// ─── History panel ───────────────────────────────────────────────────────────

let historyPage = 1;

function showHistory(): void {
  const existing = document.getElementById('history-panel');
  if (existing) {
    existing.remove();
    return;
  }

  const panel = document.createElement('div');
  panel.id = 'history-panel';
  panel.className = 'history-panel';
  panel.innerHTML = `
    <div class="history-header">
      <h2>History</h2>
      <button class="history-close" id="history-close">✕</button>
    </div>
    <div class="history-body" id="history-body">
      <p class="history-loading">Loading…</p>
    </div>
    <div class="history-footer" id="history-footer"></div>
  `;
  document.body.appendChild(panel);
  requestAnimationFrame(() => panel.classList.add('open'));

  document.getElementById('history-close')!.addEventListener('click', () => {
    panel.classList.remove('open');
    setTimeout(() => panel.remove(), 250);
  });

  historyPage = 1;
  void loadHistoryPage();
}

async function loadHistoryPage(): Promise<void> {
  if (!activeApi) return;
  const body = document.getElementById('history-body');
  const footer = document.getElementById('history-footer');
  if (!body || !footer) return;

  body.innerHTML = '<p class="history-loading">Loading…</p>';

  try {
    const res = await activeApi.history(historyPage, 20);
    if (res.rounds.length === 0) {
      body.innerHTML = '<p class="history-loading">No rounds yet.</p>';
      footer.innerHTML = '';
      return;
    }

    const currency = activeSession?.player.currency ?? 'USD';
    body.innerHTML = `
      <table class="history-table">
        <thead><tr><th>Game</th><th>Bet</th><th>Win</th><th>Date</th></tr></thead>
        <tbody>${res.rounds
          .map(
            (r: HistoryRound) => `
          <tr>
            <td>${r.gameCode}</td>
            <td>${formatMoney(r.betAmount, currency)}</td>
            <td class="${BigInt(r.winAmount) > 0n ? 'win' : ''}">${formatMoney(r.winAmount, currency)}</td>
            <td>${new Date(r.startedAt).toLocaleString()}</td>
          </tr>
        `,
          )
          .join('')}
        </tbody>
      </table>
    `;

    const totalPages = Math.ceil(res.total / res.pageSize);
    footer.innerHTML = '';
    if (totalPages > 1) {
      const prev = document.createElement('button');
      prev.className = 'shell-btn';
      prev.textContent = '← Prev';
      prev.disabled = historyPage <= 1;
      prev.addEventListener('click', () => {
        historyPage--;
        void loadHistoryPage();
      });

      const info = document.createElement('span');
      info.className = 'history-page-info';
      info.textContent = `${historyPage} / ${totalPages}`;

      const next = document.createElement('button');
      next.className = 'shell-btn';
      next.textContent = 'Next →';
      next.disabled = historyPage >= totalPages;
      next.addEventListener('click', () => {
        historyPage++;
        void loadHistoryPage();
      });

      footer.append(prev, info, next);
    }
  } catch {
    body.innerHTML = '<p class="history-loading" style="color:#e06767">Failed to load history.</p>';
  }
}

// ─── Paytable modal ──────────────────────────────────────────────────────────

function showPaytable(session: SessionInitResponse): void {
  const existing = document.getElementById('paytable-overlay');
  if (existing) {
    existing.remove();
    return;
  }

  const overlay = document.createElement('div');
  overlay.id = 'paytable-overlay';
  overlay.className = 'paytable-overlay';

  let content = '';
  const game = session.game;
  // The paytable is per game by definition, so it reads the game's own
  // `config` rather than anything the platform understands.
  const config = game.config;
  const edgePct = ((game.houseEdgeBps ?? 0) / 100).toFixed(2);

  if (game.gameCode === 'dice') {
    content = `
      <h2>Provably Fair Dice – Rules</h2>
      <div class="pt-section">
        <p>Choose OVER or UNDER and a target value (1–98). Roll to win.</p>
        <p>The multiplier is calculated from your win probability and house edge.</p>
        <p>Lower probability = higher multiplier. All rolls are provably fair via HMAC.</p>
        <p>House edge: ${edgePct}%</p>
      </div>
    `;
  } else if (game.gameCode === 'blackjack') {
    content = `
      <h2>Blackjack – Rules</h2>
      <div class="pt-section">
        <p>Beat the dealer without going over 21. Face cards are worth 10; Aces are 1 or 11.</p>
        <p>Natural blackjack pays <strong>3:2</strong>. Wins pay even money. Pushes return your stake.</p>
        <p>Dealer stands on all 17s. Double on any first two cards. Split equal ranks once (Aces get one card each).</p>
        <p>Insurance (half stake) is offered when the dealer shows an Ace; pays 2:1 if dealer has blackjack.</p>
        <p>Max payout: ${str(config, 'maxPayout', '—')}</p>
      </div>
    `;
  } else if (game.gameCode === 'hype-spin') {
    content = `
      <h2>HypeSpin – Rules</h2>
      <div class="pt-section">
        <p>Set your win chance (gold arc on the wheel). Lower chance = higher multiplier.</p>
        <p>Spin the wheel. If the pointer lands in the gold arc, you win.</p>
        <p>Multiplier ≈ (100% − house edge) ÷ win chance. House edge: ${edgePct}%</p>
        <p>Win chance range: ${str(config, 'minWinChance')}% – ${str(config, 'maxWinChance')}%</p>
        <p>All spins are provably fair via HMAC-SHA256.</p>
      </div>
    `;
  }

  overlay.innerHTML = `
    <div class="paytable-card">
      <button class="paytable-close" id="paytable-close">✕</button>
      ${content}
    </div>
  `;
  document.body.appendChild(overlay);

  document.getElementById('paytable-close')!.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
}

// ─── Lobby / game lifecycle ──────────────────────────────────────────────────

function returnToLobby(): void {
  document.getElementById('history-panel')?.remove();
  document.getElementById('paytable-overlay')?.remove();

  const params = new URLSearchParams(window.location.search);
  const lobbyUrl = params.get('lobbyUrl') ?? activeSession?.lobbyUrl;

  activeRenderer?.destroy();
  activeRenderer = null;
  activeGame = null;
  activeSession = null;
  hideShell();

  if (lobbyUrl) {
    window.location.href = lobbyUrl;
    return;
  }

  const api = activeApi ?? new GameApi();
  activeApi = api;
  showLobby(api, (session) => launchGame(api, session));
}

async function launchGame(api: GameApi, session: SessionInitResponse): Promise<void> {
  hideLobby();
  activeApi = api;
  activeSession = session;

  /**
   * A session can be opened for any game the platform routes, which is more
   * games than this client draws. A game with no scene of its own is played
   * through the schema-driven form; only a shared round — a crash lobby, a
   * head-to-head match — has nothing to fall back to, and saying so is
   * better than a blank canvas and a thrown import.
   */
  if (!hasScene(session.game.gameCode) && !isGeneric(session.game.mode)) {
    activeSession = null;
    showNotice(
      'No client for this game',
      `${session.game.name} is available on this platform, but this client build has no UI for it. Play it from the operator's own client.`,
      session.game.gameCode,
    );
    return;
  }

  showShell(session);

  const gameDiv = document.getElementById('game');
  if (!gameDiv) throw new Error('missing #game element');

  if (!hasScene(session.game.gameCode)) {
    gameDiv.classList.add('gen-host');
    activeRenderer = mountGenericGame({
      api,
      session,
      host: gameDiv,
      onBalance: (balance) => updateShellBalance(balance, session.player.currency),
    });
    return;
  }

  const bundle = await loadGame(session.game.gameCode);
  const data: SceneData = { api, session };

  activeGame = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'game',
    width: bundle.width,
    height: bundle.height,
    backgroundColor: '#06110f',
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    scene: [bundle.scene],
    callbacks: {
      preBoot: (game) => game.scene.start(bundle.sceneKey, data),
    },
  });

  const phaser = activeGame;
  activeRenderer = { destroy: () => phaser.destroy(true) };

  if (getSoundMuted()) activeGame.sound.mute = true;
}

// ─── Entry point ─────────────────────────────────────────────────────────────

async function main(): Promise<void> {
  const params = new URLSearchParams(window.location.search);

  const launchToken = params.get('token');
  const api = new GameApi();
  activeApi = api;

  if (!launchToken) {
    showLobby(api, (session) => launchGame(api, session));
    return;
  }

  try {
    const session = await api.openSession(launchToken);
    await launchGame(api, session);
  } catch (error) {
    if (error instanceof GameApiError && error.code === 'TOKEN_ALREADY_USED') {
      showNotice(
        'Launch token already used',
        'Launch tokens are single use, which is what stops a shared URL from opening someone else\u2019s session. Generate a fresh one and try again.',
        'pnpm --filter @gp/operator-cli start launch',
      );
      return;
    }

    showNotice(
      'Could not start the game',
      error instanceof Error ? error.message : 'Unknown error',
      'Check that the game API and the mock operator wallet are running:\n' + 'pnpm dev:services',
    );
  }
}

void main();
